import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { canvasZoomProofWorldPx } from '../common/util';

const FADE_OUT_MS = 1500;

export interface CanvasClickMarkerProps {
	worldX: number;
	worldY: number;
	/** Bumps on each placement so the hide timer restarts even when x/y are unchanged. */
	placementGeneration: number;
	/** Canvas zoom (`transform: scale`) so marker sizes stay readable when zoomed out. */
	scale: number;
}

export function CanvasClickMarker({ worldX, worldY, placementGeneration, scale }: CanvasClickMarkerProps) {
	const [isVisible, setIsVisible] = useState(true);

	const outer = canvasZoomProofWorldPx(44, scale);
	const halo = canvasZoomProofWorldPx(34, scale);
	const dot = canvasZoomProofWorldPx(9, scale);
	const ring = canvasZoomProofWorldPx(1, scale);

	useEffect(() => {
		setIsVisible(true);
		const id = window.setTimeout(() => setIsVisible(false), FADE_OUT_MS);
		return () => window.clearTimeout(id);
	}, [worldX, worldY, placementGeneration]);

	return (
		<Box
			sx={{
				position: 'absolute',
				left: '50%',
				top: '50%',
				width: outer,
				height: outer,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				pointerEvents: 'none',
				zIndex: 0,
				opacity: isVisible ? 1 : 0,
				transition: (theme) =>
					theme.transitions.create('opacity', {
						duration: theme.transitions.duration.short,
						easing: theme.transitions.easing.easeInOut,
					}),
			}}
			style={{
				transform: `translate(calc(-50% + ${worldX}px), calc(-50% + ${worldY}px))`,
			}}
		>
			<Box
				sx={(theme) => {
					const color = theme.palette.primary.main;
					return {
						position: 'absolute',
						width: halo,
						height: halo,
						borderRadius: '50%',
						backgroundColor: alpha(color, 0.28),
						transformOrigin: 'center center',
						'@keyframes canvasMarkerHaloPulse': {
							'0%, 100%': { transform: 'scale(0.88)' },
							'50%': { transform: 'scale(1.22)' },
						},
						animation: 'canvasMarkerHaloPulse 2.6s ease-in-out infinite',
					};
				}}
			/>
			<Box
				sx={{
					position: 'relative',
					width: dot,
					height: dot,
					borderRadius: '50%',
					bgcolor: 'primary.main',
					zIndex: 1,
					boxShadow: (theme) => `0 0 0 ${ring}px ${alpha(theme.palette.background.paper, 0.35)}`,
				}}
			/>
		</Box>
	);
}

export default CanvasClickMarker;
